import { ApiTranslation } from '../api-translation';
import { ToWinTooltip, useToWinQuote } from '../to-win-tooltip';
import { getOptionTone } from './option-tone';
import { useModelStore } from '@/client/store/model-store';
import { useTradeResumeStore } from '@/client/store/trade-resume-store';
import { OptionSide, useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { HtmlHTMLAttributes, MouseEvent, ReactNode, useMemo, useRef, useState } from 'react';
import useHoverDirty from 'react-use/lib/useHoverDirty';
import { useLocation } from 'wouter';

type OptionButtonProps = HtmlHTMLAttributes<HTMLButtonElement> & {
  marketAddress: string;
  marketDecimal: number;
  optionAddress: string;
  optionName: string;
  optionDescription: string;
  optionPrice: number;
  optionDecimal: number;
  active?: boolean;
  openTrade?: boolean;
  before?: ReactNode;
  after?: ReactNode;
  showDescription?: boolean;
  children?: ReactNode;
  /** For multi-option markets: specify yes/no side */
  optionSide?: OptionSide;
  /** API fallback for when on-chain options() reverts */
  apiOptionList?: Array<{ optionAddress: string }>;
  /**
   * Home page cards: a click opens this page with the option preselected (`?side=<option address>`)
   * instead of the trade sheet, and a hover loads no "to win" quote.
   */
  navigateTo?: string;
};
export const OptionButton = ({
  marketAddress,
  marketDecimal,
  optionAddress,
  optionName,
  optionDescription,
  optionDecimal,
  active,
  openTrade = true,
  className,
  onClick,
  before,
  after,
  showDescription = true,
  children,
  optionSide,
  apiOptionList,
  navigateTo,
  ...attrs
}: OptionButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isHover = useHoverDirty(ref);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [, navigate] = useLocation();
  // Single quote per button, shared with the tooltip (each useToWinQuote
  // mounts two query observers, so a second instance doubles the idle cost).
  // A navigating button shows no quote, so it reads nothing from the chain.
  const toWinQuote = useToWinQuote({
    enabled: !navigateTo && (isHover || tooltipOpen),
    marketAddress,
    marketDecimal,
    optionAddress,
    optionDecimal,
    apiOptionList,
  });
  const tone = useMemo(
    () => getOptionTone(optionName) ?? getOptionTone(optionDescription) ?? getOptionTone(optionSide),
    [optionDescription, optionName, optionSide]
  );
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  const setTradeParams = useTradeParamsStore(state => state.setTradeParams);
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (navigateTo) {
      // Stop here: the card's own click would navigate a second time.
      e.preventDefault();
      e.stopPropagation();
      navigate(`${navigateTo}${navigateTo.includes('?') ? '&' : '?'}side=${encodeURIComponent(optionAddress)}`);
    } else if (openTrade) {
      e.preventDefault();
      e.stopPropagation();
      useTradeResumeStore.getState().discardSuspendedTrade();
      setTradeOpen(true);
      setTradeParams({ type: 'buy', marketAddress, optionAddress, optionSide, amount: BigInt(0) });
    }
  };
  return (
    <ToWinTooltip open={!navigateTo && tooltipOpen} onOpenChange={setTooltipOpen} quote={toWinQuote}>
      <button
        ref={ref}
        type="button"
        {...attrs}
        className={cn(
          'rounded-1.5 flex-shrink-0 px-4 py-3 truncate flex items-center justify-center transition-colors',
          // Yes/Option1 - Green
          tone === 'yes' && 'text-emerald-600 bg-emerald-500/15',
          tone === 'yes' && 'hover:bg-emerald-600 hover:text-white',
          tone === 'yes' && active && 'bg-emerald-600 text-white',
          // No/Option2 - Red
          tone === 'no' && 'text-red-500 bg-red-500/15',
          tone === 'no' && 'hover:bg-red-600 hover:text-white',
          tone === 'no' && active && 'bg-red-600 text-white',
          className
        )}
        onClick={handleClick}
      >
        {isHover && !navigateTo ? (
          (toWinQuote.label ?? (toWinQuote.isLoading ? '...' : optionDescription || optionName))
        ) : (
          <>
            {before}
            {children ? (
              children
            ) : showDescription ? (
              <span className="mx-1">
                <ApiTranslation>{optionDescription || optionName}</ApiTranslation>
              </span>
            ) : null}
            {after}
          </>
        )}
      </button>
    </ToWinTooltip>
  );
};
